import { Component, Input, Output, EventEmitter, ViewChild, ViewChildren, QueryList, ElementRef, OnChanges, SimpleChanges, ViewEncapsulation, AfterViewInit, ChangeDetectorRef, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common'; import { Renderer2 } from '@angular/core';
import { DataViewModule } from 'primeng/dataview';
import { ViewportService } from 'src/app/services/viewport.service';
import { ButtonModule } from 'primeng/button'; import { SkeletonModule } from 'primeng/skeleton';
import { Item } from 'src/app/models/liveshop'; import { ChipModule } from 'primeng/chip';
import { DialogModule } from 'primeng/dialog'; import { MultiSelect } from 'primeng/multiselect';
import { VideoModalService } from 'src/app/services/video-modal.service';
import { PanelModule } from 'primeng/panel';
import { MultiSelectModule } from 'primeng/multiselect'; import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { ProductService } from 'src/app/services/product.service';
import { take } from 'rxjs/operators';

type PlacedItem = VideoItem & { __cls: string };

type MosaicPage = {
  id: string;
  items: VideoItem[];
  placed: PlacedItem[];
};

type HeaderChip =
  | { type: 'cat'; value: number; label: string }
  | { type: 'inf'; value: string; label: string };

export interface VideoItem {
  uid: string;
  id: number;
  src: string;
  poster: string;
  thumbnail?: string;
  products?: Array<any>;
}

@Component({
  selector: 'app-discover',
  standalone: true,
  templateUrl: './discover.component.html',
  styleUrls: ['./discover.component.scss'],
  imports: [CommonModule, DataViewModule, ButtonModule, DialogModule, PanelModule, SkeletonModule, MultiSelectModule, ChipModule, FormsModule, InputTextModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated
})
export class DiscoverComponent implements OnChanges, OnDestroy, AfterViewInit {

  @Input() design: 'grid' | 'book' = 'grid';
  @Input() iconMode: 'cart' | 'bag' = 'cart';
  @Input() showFilterBar = true;
  @Input() arraySlicksFull: Item[] = [];
  @Input() videoUrls: VideoItem[] = [];
  @Input() isAdmin: boolean = true;
  @Input() loading: boolean = false;
  @Input() isCol4: boolean = false;
  @Input() widget_id?: number | undefined;
  @Input() store_id?: number | undefined;
  @Input() height?: number | undefined;
  @Input() templateType: string | undefined;
  @Input() hasMoreVideos: boolean = true;
  @Output() loadMoreVideos = new EventEmitter<void>();
  @Output() openSidebar = new EventEmitter<void>();
  @Input() totalAssets: number = 0;
  @Input() pageToVideos: number = 0;
  @Input() instanceId?: string;
  @Input() discoverLayout: 'mosaic' | 'grid' = 'mosaic';
  @Input() showPostItemBadge = true;
  hasLoadedOnce = false;

  // filter bar state
  filterBarExpanded = false;
  filterPanelOpen = false;

  toggleFilterBarExpanded(): void {
    this.filterBarExpanded = !this.filterBarExpanded;
    if (!this.filterBarExpanded) this.filterPanelOpen = false;
  }

  toggleFilterPanel(): void {
    this.filterBarExpanded = true;
    const willOpen = !this.filterPanelOpen;
    this.filterPanelOpen = willOpen;
    if (willOpen) this.reloadFilterOptionsOnOpen();
  }

  private reloadFilterOptionsOnOpen(): void {
    this.catQuery = '';
    this.infQuery = '';
    this.resetCategoriesLazy();
    this.resetInfluencersLazy();
    this.loadNextCategoriesPage(true);
    this.loadNextInfluencersPage(true);
    this.cdr.markForCheck();
  }

  @ViewChild('catMs') catMs?: MultiSelect;
  @ViewChild('infMs') infMs?: MultiSelect;

  msMount = true;

  closeFilterPanel(): void {
    this.filterPanelOpen = false;
    this.filterBarExpanded = false;
    this.msMount = false;
    this.cdr.detectChanges();
    this.catQuery = '';
    this.infQuery = '';
    this.resetCategoriesLazy();
    this.resetInfluencersLazy();
    setTimeout(() => {
      this.msMount = true;
      this.cdr.markForCheck();
    }, 0);
  }

  private resetCategoriesLazy(): void {
    this.catPage = 1;
    this.catTotal = 0;
    this.catHasMore = true;
    this.catLoadedPages.clear();
    this.categoryOptions = [];
    this.catUserScrolled = false;
    this.loadNextCategoriesPage(true);
  }

  private resetInfluencersLazy(): void {
    this.infPage = 1;
    this.infTotal = 0;
    this.infHasMore = true;
    this.infLoadedPages.clear();
    this.influencerOptions = [];
    this.infUserScrolled = false;
    this.loadNextInfluencersPage(true);
  }

  private forceClearMultiSelectFilter(ms?: MultiSelect): void {
    if (!ms) return;
    (ms as any).filterValue = '';
    ms.resetFilter?.();
    (ms as any).onFilter?.({ filter: '' });
    const input: HTMLInputElement | null =
      ms.el?.nativeElement?.querySelector?.('.p-multiselect-filter') ?? null;
    if (input) {
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('keyup', { bubbles: true }));
    }
  }

  constructor(
    private renderer: Renderer2,
    private productService: ProductService,
    private viewportService: ViewportService,
    private videoModalService: VideoModalService,
    private cdr: ChangeDetectorRef
  ) { }

  syncMultiSelectPanelWidth(ms: any, panelSelector: string = '.ss-discover-ms-panel') {
    const host: HTMLElement | null = ms?.el?.nativeElement ?? null;
    if (!host) return;
    const w = Math.round(host.getBoundingClientRect().width);
    const panel = document.querySelector(panelSelector) as HTMLElement | null;
    if (!panel) return;
    this.renderer.setStyle(panel, 'width', `${w}px`);
    this.renderer.setStyle(panel, 'minWidth', `${w}px`);
    this.renderer.setStyle(panel, 'boxSizing', 'border-box');
  }

  openModal(video: VideoItem) {
    void this.videoModalService.open({
      video,
      slicks: this.arraySlicksFull,
      totalAssets: this.totalAssets,
      isAdmin: this.isAdmin,
      widgetId: this.widget_id,
      storeId: this.store_id,
      height: this.height,
      pageToVideos: this.pageToVideos,
      instanceId: this.instanceId
    });
    this.cdr.detectChanges();
  }

  ngAfterViewInit(): void {
    this.viewportService.setTemplateType(this.templateType);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const loadingChange = changes['loading'];
    if (loadingChange) {
      const prevLoading = !!loadingChange.previousValue;
      const currLoading = !!loadingChange.currentValue;
      console.log('[Discover] loading changed:', prevLoading, '->', currLoading);
      if (prevLoading === true && currLoading === false) {
        if (this.isLoadingSlides) this.isLoadingSlides = false;
        this.cdr.markForCheck();
      }
    }

    const listChanged = !!changes['videoUrls'];
    if (listChanged) {
      console.log('[Discover] videoUrls changed, length:', this.videoUrls?.length, 'hasLoadedOnce:', this.hasLoadedOnce);
      if ((this.videoUrls?.length || 0) > 0) {
        this.hasLoadedOnce = true;
      }
    }
    console.log('[Discover] showFilterBar:', this.showFilterBar, 'activeFiltersCount:', this.activeFiltersCount, 'selectedCategories:', this.selectedCategories?.length, 'selectedInfluencers:', this.selectedInfluencers?.length);
    const layoutChanged = !!changes['discoverLayout'];
    const colChange = changes['isCol4'];

    if (layoutChanged && this.discoverLayout !== 'mosaic') {
      this.pages = [];
      this.lastLen = 0;
      return;
    }

    // switching back to mosaic: rebuild pages from full list
    if (layoutChanged && this.discoverLayout === 'mosaic') {
      this.hardResetPagesFromFullList(this.videoUrls ?? []);
      return;
    }

    // isCol4 change affects isMobile → pageSize and preset change, rebuild
    if (colChange && this.discoverLayout === 'mosaic') {
      this.hardResetPagesFromFullList(this.videoUrls ?? []);
      return;
    }

    if (!listChanged || this.discoverLayout !== 'mosaic') return;

    const curr = this.videoUrls ?? [];
    const prevLen = this.lastLen;
    const currLen = curr.length;

    if (prevLen === 0 && currLen > 0) {
      this.hardResetPagesFromFullList(curr);
      return;
    }

    if (currLen < prevLen) {
      this.hardResetPagesFromFullList(curr);
      return;
    }

    // append-only
    let remaining = curr.slice(prevLen);
    if (!remaining.length) return;

    this.isLoadingSlides = false;

    let firstNewPageId: string | null = null;

    // Primeiro completa a última página existente (se ela ainda tiver espaço livre),
    // reconstruindo o mosaico inteiro dela para os tiles ficarem na posição certa do preset.
    const lastPage = this.pages[this.pages.length - 1];
    if (lastPage && lastPage.items.length < this.pageSize) {
      const freeSlots = this.pageSize - lastPage.items.length;
      const toFill = remaining.slice(0, freeSlots);
      if (toFill.length) {
        lastPage.items = [...lastPage.items, ...toFill];
        lastPage.placed = this.buildMosaicItems(lastPage.items);
        firstNewPageId = lastPage.id;
        remaining = remaining.slice(toFill.length);
      }
    }

    for (let i = 0; i < remaining.length; i += this.pageSize) {
      const chunk = remaining.slice(i, i + this.pageSize);
      const id = `p${Date.now()}_${Math.random().toString(16).slice(2)}`;
      if (!firstNewPageId) firstNewPageId = id;
      this.pages.push({ id, items: chunk, placed: this.buildMosaicItems(chunk) });
    }

    this.lastLen = currLen;
    this.cdr.detectChanges();

    setTimeout(() => {
      if (this.scrollOnNextAppend && firstNewPageId) {
        this.scrollOnNextAppend = false;
        this.pendingScrollPageId = firstNewPageId;
        this.scrollToPageWrap(firstNewPageId);
      }
    }, 120);
  }

  private scrollToPageWrap(pageId: string): void {
    const wrap = this.pageWraps?.toArray()
      ?.find(r => r.nativeElement.getAttribute('data-page-id') === pageId)
      ?.nativeElement;
    if (!wrap) return;
    const y = wrap.getBoundingClientRect().top + window.scrollY - this.scrollOffsetPx;
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    this.pendingScrollPageId = null;
  }

  ngOnDestroy(): void { }

  private categoriesLoadedKey = '';

  private tryLoadCategoryOptions(): void {
    const storeId = Number(this.store_id);
    const widgetId = Number(this.widget_id);
    if (!Number.isFinite(storeId) || !Number.isFinite(widgetId)) return;
    const key = `${storeId}:${widgetId}`;
    if (this.categoriesLoadedKey === key) return;
    this.categoriesLoadedKey = key;
    this.productService.listWidgetCategories(storeId, widgetId, { page: 1, limit: 100 })
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.categoryOptions = (res?.categories || []).map(c => ({
            label: c.label,
            value: Number(c.value),
          }));
          this.cdr.markForCheck();
        },
        error: () => {
          this.categoriesLoadedKey = '';
          this.cdr.markForCheck();
        }
      });
  }

  isLoadingSlides: boolean = false;
  showbtn: boolean = true;

  loadMore() {
    if (this.loading || !this.hasMoreVideos || this.isLoadingSlides) return;
    this.scrollOnNextAppend = true;
    this.isLoadingSlides = true;
    this.cdr.markForCheck();
    this.loadMoreVideos.emit();
  }

  onOpenSidebar(): void {
    this.openSidebar.emit();
  }

  trackInf = (_: number, o: { value: string }) => o.value;

  removeSelectedInfluencer(opt: { value: string }, ev: any): void {
    ev?.originalEvent?.stopPropagation?.();
    this.selectedInfluencers = (this.selectedInfluencers || []).filter(i => i.value !== opt.value);
    this.onFiltersChanged();
    this.cdr.markForCheck();
  }

  get activeFiltersCount(): number {
    let count = 0;
    if (this.selectedCategories?.length) count += 1;
    if (this.selectedInfluencers?.length) count += 1;
    return count;
  }

  selectedCategories: Array<{ label: string; value: number }> = [];
  selectedInfluencers: Array<{ label: string; value: string; avatar?: string }> = [];

  clearAllFilters(): void {
    this.selectedCategories = [];
    this.selectedInfluencers = [];
    this.emitFilters();
    this.cdr.markForCheck();
  }

  private influencerByUid = new Map<string, string>();
  influencerQuery = '';

  private normalizeText(v: string): string {
    return (v || '').toString().replace(/^@/, '').trim().toLowerCase();
  }

  onMultiClear(type: 'cat' | 'inf'): void {
    if (type === 'cat') this.selectedCategories = [];
    else this.selectedInfluencers = [];
    queueMicrotask(() => this.onFiltersChanged());
  }

  onFiltersChanged(): void {
    console.log('[Discover] onFiltersChanged - cats:', this.selectedCategories?.length, 'infs:', this.selectedInfluencers?.length, 'activeFiltersCount:', this.activeFiltersCount);
    this.isFilteringAny = true;
    this.emitFilters();
    queueMicrotask(() => {
      this.isFilteringAny = false;
      this.cdr.markForCheck();
    });
  }

  isFilteringAny = false;

  get selectedHeaderChips(): HeaderChip[] {
    const chips: HeaderChip[] = [];
    for (const c of this.selectedCategories || []) {
      chips.push({ type: 'cat', value: c.value, label: c.label });
    }
    for (const i of this.selectedInfluencers || []) {
      chips.push({ type: 'inf', value: i.value, label: i.label });
    }
    return chips;
  }

  removeChip(chip: HeaderChip): void {
    if (chip.type === 'cat') {
      this.selectedCategories = (this.selectedCategories || []).filter(c => c.value !== chip.value);
    } else {
      this.selectedInfluencers = (this.selectedInfluencers || []).filter(i => i.value !== chip.value);
    }
    this.onFiltersChanged();
  }

  trackChip = (_: number, c: HeaderChip) => `${c.type}:${c.value}`;

  @Output() filtersChange = new EventEmitter<{ categoryIds: number[]; influencerHandles: string[] }>();

  private emitFilters(): void {
    this.filtersChange.emit({
      categoryIds: (this.selectedCategories || []).map(x => x.value),
      influencerHandles: (this.selectedInfluencers || []).map(x => x.value),
    });
  }

  private hoverOpenTimer: any;
  private hoverCloseTimer: any;

  onFilterBarMouseEnter(): void {
    if (this.hoverCloseTimer) clearTimeout(this.hoverCloseTimer);
    this.hoverOpenTimer = setTimeout(() => {
      if (!this.filterBarExpanded) {
        this.filterBarExpanded = true;
        this.cdr.markForCheck();
      }
    }, 80);
  }

  onFilterBarMouseLeave(): void {
    if (this.filterPanelOpen) return;
    if (this.hoverOpenTimer) clearTimeout(this.hoverOpenTimer);
    this.hoverCloseTimer = setTimeout(() => {
      this.filterBarExpanded = false;
      this.cdr.markForCheck();
    }, 180);
  }

  trackVideo = (_: number, v: VideoItem) => v.uid;
  skeletonItems = Array.from({ length: 10 }, (_, i) => i);
  trackSkel = (_: number, i: number) => i;

  get showSkeleton(): boolean {
    // Só mostra o skeleton no primeiro carregamento (grid ainda vazia) — no
    // "carregar mais" os itens já carregados não podem sumir, só o botão
    // de load more mostra o próprio spinner via isLoadingSlides.
    return this.loading && (this.videoUrls?.length || 0) === 0;
  }

  // influencers lazy
  infLoading = false;
  private infQuery = '';
  private infLimit = 10;
  private infPage = 1;
  private infTotal = 0;
  private infHasMore = true;
  private infLoadedPages = new Set<number>();
  private infUserScrolled = false;

  onInfluencerPanelShow(ms: any) {
    this.syncMultiSelectPanelWidth(ms, '.ss-discover-ms-panel--inf');
    if (!this.influencerOptions.length) {
      this.infUserScrolled = false;
      this.loadNextInfluencersPage(true);
    }
  }

  onInfluencerLazyLoad(ev: any) {
    if (this.infLoading || !this.infHasMore) return;
    const first = Number(ev?.first ?? 0) || 0;
    const rows = Number(ev?.rows ?? 0) || 10;
    if (first === 0 && !this.infUserScrolled) return;
    if (first > 0) this.infUserScrolled = true;
    const nearEnd = first + rows >= (this.influencerOptions.length - 2);
    if (!nearEnd) return;
    this.loadNextInfluencersPage();
  }

  onInfluencerFilter(ev: any) {
    const q = String(ev?.filter ?? '').trim();
    this.infQuery = q;
    this.infPage = 1;
    this.infTotal = 0;
    this.infHasMore = true;
    this.infLoadedPages.clear();
    this.influencerOptions = [];
    this.infUserScrolled = false;
    this.loadNextInfluencersPage(true);
  }

  influencerOptions: Array<{ label: string; value: string; avatar?: string }> = [];

  private loadNextInfluencersPage(force = false) {
    if (this.infLoading || (!this.infHasMore && !force)) return;
    const storeId = Number(this.store_id);
    const widgetId = Number(this.widget_id);
    if (!Number.isFinite(storeId) || !Number.isFinite(widgetId)) return;
    const page = this.infPage;
    if (!force && this.infLoadedPages.has(page)) return;
    this.infLoadedPages.add(page);
    this.infLoading = true;
    this.cdr.markForCheck();
    this.productService.listWidgetInfluencers(storeId, widgetId, {
      page,
      limit: this.infLimit,
      q: this.infQuery
    })
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          const items = res?.influencers ?? [];
          const total = Number(res?.total ?? 0);
          const hasMore = !!(res as any)?.hasMore;
          const respPage = Number((res as any)?.page ?? page);
          if (this.infTotal === 0 && total) this.infTotal = total;
          this.influencerOptions = [
            ...this.influencerOptions,
            ...items.map(i => ({
              label: i.label,
              value: i.value,
              avatar: i.avatar || undefined
            }))
          ];
          this.infPage = Math.max(1, respPage) + 1;
          this.infHasMore = hasMore;
          this.infLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.infLoadedPages.delete(page);
          this.infLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  // categories lazy
  catLoading = false;
  private catQuery = '';
  private catLimit = 20;
  private catPage = 1;
  private catTotal = 0;
  private catHasMore = true;
  private catLoadedPages = new Set<number>();
  private catUserScrolled = false;

  categoryOptions: Array<{ label: string; value: number }> = [];

  onCategoryPanelShow(ms: any) {
    this.syncMultiSelectPanelWidth(ms, '.ss-discover-ms-panel--cat');
    if (!this.categoryOptions.length) {
      this.catUserScrolled = false;
      this.loadNextCategoriesPage(true);
    }
  }

  onCategoryLazyLoad(ev: any) {
    if (this.catLoading || !this.catHasMore) return;
    const first = Number(ev?.first ?? 0) || 0;
    const rows = Number(ev?.rows ?? 0) || 10;
    if (first === 0 && !this.catUserScrolled) return;
    if (first > 0) this.catUserScrolled = true;
    const nearEnd = first + rows >= (this.categoryOptions.length - 2);
    if (!nearEnd) return;
    this.loadNextCategoriesPage();
  }

  onCategoryFilter(ev: any) {
    const q = String(ev?.filter ?? '').trim();
    this.catQuery = q;
    this.catPage = 1;
    this.catTotal = 0;
    this.catHasMore = true;
    this.catLoadedPages.clear();
    this.categoryOptions = [];
    this.catUserScrolled = false;
    this.loadNextCategoriesPage(true);
  }

  private loadNextCategoriesPage(force = false) {
    if (this.catLoading || (!this.catHasMore && !force)) return;
    const storeId = Number(this.store_id);
    const widgetId = Number(this.widget_id);
    if (!Number.isFinite(storeId) || !Number.isFinite(widgetId)) return;
    const page = this.catPage;
    if (!force && this.catLoadedPages.has(page)) return;
    this.catLoadedPages.add(page);
    this.catLoading = true;
    this.cdr.markForCheck();
    this.productService.listWidgetCategories(storeId, widgetId, {
      page,
      limit: this.catLimit,
      q: this.catQuery
    })
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          const items = res?.categories ?? [];
          const total = Number(res?.total ?? 0);
          const hasMore = !!(res as any)?.hasMore;
          const respPage = Number((res as any)?.page ?? page);
          if (this.catTotal === 0 && total) this.catTotal = total;
          this.categoryOptions = [
            ...this.categoryOptions,
            ...items.map((c: any) => ({
              label: c.label,
              value: Number(c.value),
            }))
          ];
          this.catPage = Math.max(1, respPage) + 1;
          this.catHasMore = hasMore;
          this.catLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.catLoadedPages.delete(page);
          this.catLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  // mosaic pages
  trackPage = (_: number, p: MosaicPage) => p.id;

  pages: MosaicPage[] = [];
  private lastLen = 0;

  private get pageSize(): number {
    const fallback = this.pageToVideos || (this.isMobile ? 3 : 10);
    return Math.max(1, Number(fallback));
  }

  get isMobile(): boolean {
    return this.isCol4 || window.matchMedia('(max-width: 768px)').matches;
  }

  private readonly MOSAIC_PRESET_10: ReadonlyArray<string> = [
    'tile-1x2', 'tile-1x1', 'tile-1x1',
    'tile-1x2', 'tile-1x2', 'tile-1x2',
    'tile-1x1', 'tile-1x2', 'tile-1x1', 'tile-2x1'
  ];
  private readonly MOSAIC_MOBILE_3: ReadonlyArray<string> = ['tile-1x2', 'tile-1x1', 'tile-1x1'];

  private buildMosaicItems(items: VideoItem[]): PlacedItem[] {
    const preset = this.isMobile ? this.MOSAIC_MOBILE_3 : this.MOSAIC_PRESET_10;
    return items.map((item, i) => ({
      ...item,
      __cls: preset[i] ?? 'tile-1x1',
    }));
  }

  private hardResetPagesFromFullList(full: VideoItem[]): void {
    this.pages = [];
    this.lastLen = 0;
    const list = full ?? [];
    for (let i = 0; i < list.length; i += this.pageSize) {
      const chunk = list.slice(i, i + this.pageSize);
      const id = `p${i}_${Math.random().toString(16).slice(2)}`;
      this.pages.push({ id, items: chunk, placed: this.buildMosaicItems(chunk) });
    }
    this.lastLen = list.length;
    this.cdr.detectChanges();
  }

  @ViewChildren('pageWrap') pageWraps?: QueryList<ElementRef<HTMLElement>>;

  private scrollOnNextAppend = false;
  private pendingScrollPageId: string | null = null;

  private get scrollOffsetPx(): number {
    return this.isCol4 ? 56 : 72;
  }
}
